/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import GradientBorder from "examples/GradientBorder";
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import CoverLayout from "layouts/authentication/components/CoverLayout";
// import bgSignIn from "assets/images/signInImage.png";
// Images
import bgSignIn from "assets/images/sarto-bg.png";
import { Formik } from "formik";
import { SignInSchema } from "validations";
import useCommon from "hooks/useCommon";
import { loginUser } from "store/slice/userSlice";
import VuiAlert from "components/VuiAlert";
import { Checkbox, CircularProgress, Grid, InputAdornment } from "@mui/material";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import PasswordInput from "components/PaaswordInput";
import { setLayout } from "context";
import { clearAuthMessage } from "store/slice/userSlice";
import usePermissions from "hooks/usePermissions";
import Cookies from "js-cookie";
import { forgotPassword } from "store/slice/userSlice";
import { ForgotPasswordSchema } from "validations";
import { resetForgotPasswordState } from "store/slice/userSlice";
import AlertMessage from "components/AlertMessage/AlertMessage";
import { color } from "@mui/system";
import { getHousekeepingNotifications } from "store/slice/pusherSlice";

function SignIn() {
  const { DISPATCH, STORE, NAVIGATE } = useCommon();
  const { getFirstAllowedRoute } = usePermissions();
  const {
    loginLoading,
    forgotPasswordLoading,
    forgotPasswordSuccess,
    forgotPasswordMessage,
    forgotPasswordError,
    loginError,
    user,
    authInitialized,
  } = STORE((state) => state.user);

  const [error, setError] = useState(null);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [showPassword, setShowPassword] = useState(false);
  const { dark, light, primary } = palette;

  const hasUserCookie = Boolean(Cookies.get("user_data"));

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSignIn = async (values, { setSubmitting }) => {
    const { email, password } = values;
    DISPATCH(clearAuthMessage());
    try {
      DISPATCH(loginUser({ email, password }))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            DISPATCH(
              getHousekeepingNotifications({
                page: 1,
                pageSize: 20,
                unreadOnly: false,
              })
            );
          } else {
            setError(res?.message);
          }
        })
        .catch((err) => {
          setError(err?.message);
        });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (values, { setSubmitting, resetForm }) => {
    try {
      await DISPATCH(forgotPassword({ email: values.email })).unwrap();

      // Optional: switch back to login after success
      // setMode("login");

      resetForm();
    } catch (err) {
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = mode === "login" ? loginLoading : forgotPasswordLoading;

  const buttonText =
    mode === "login"
      ? loginLoading
        ? "Signing In..."
        : "Sign In"
      : forgotPasswordLoading
        ? "Sending..."
        : "Send Reset Link";

  if (!authInitialized) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={40} color="primary" />
      </VuiBox>
    );
  }

  if (user) {
    return <Navigate to={getFirstAllowedRoute()} replace />;
  }

  // useEffect(() => {
  //   if (user) {
  //     const redirectTo = getFirstAllowedRoute();
  //     NAVIGATE(redirectTo, { replace: true });
  //   }
  // }, [user]);

  return (
    <CoverLayout
      image={bgSignIn}
      title="Welcome Back!"
      color="light"
      description={
        mode === "login"
          ? "Enter your email and password to sign in"
          : "Enter your email to receive password reset link"
      }
      cardContent
    >
      <Grid container justifyContent="center" alignItems="center">
        <Grid
          item
          xs={12}
          sm={8}
          md={10}
          lg={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <VuiBox sx={{ width: "100%", maxWidth: "420px", mb: 1 }}>
            {loginError && (
              <VuiAlert color="error" dismissible size="sm">
                {loginError.message
                  ? loginError.message
                  : "Network error occurred during login. Please try again."}
              </VuiAlert>
            )}

            {mode === "forgot" && forgotPasswordSuccess && (
              <AlertMessage
                message={forgotPasswordMessage || "Reset link sent. Please check your email."}
                type="success"
              />
            )}

            {mode === "forgot" && forgotPasswordError && (
              <VuiAlert color="error" dismissible size="sm">
                {forgotPasswordError}
              </VuiAlert>
            )}

            {/* {authMessage && (
              <VuiAlert
                color={authMessageType || "info"}
                dismissible
                size="sm"
                onClose={() => DISPATCH(clearAuthMessage())}
              >
                {authMessage}
              </VuiAlert>
            )} */}
          </VuiBox>

          {/* {loginError && (
            <VuiAlert color="error" dismissible size="sm">
              {loginError.message}
            </VuiAlert>
          )}
          {authMessage && (
            <VuiAlert
              color={authMessageType || "info"}
              dismissible
              size="sm"
              onClose={() => DISPATCH(clearAuthMessage())}
            >
              {authMessage}
            </VuiAlert>
          )} */}

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={mode === "login" ? SignInSchema : ForgotPasswordSchema}
            onSubmit={mode === "login" ? handleSignIn : handleForgotPassword}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              resetForm,
            }) => {
              const handleModeChange = (nextMode) => {
                resetForm();
                DISPATCH(resetForgotPasswordState());
                DISPATCH(clearAuthMessage());
                setMode(nextMode);
              };

              return (
                <VuiBox
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  role="form"
                  sx={{
                    width: "100%",
                    maxWidth: "420px",
                    p: { xs: 3, sm: 4, md: "25px", lg: "40px" },
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: borders.borderRadius.lg,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                  }}
                >
                  {/* ================= EMAIL FIELD ================= */}
                  <VuiBox mb={2}>
                    <VuiBox mb={1} ml={0.5}>
                      <VuiTypography
                        component="label"
                        variant="button"
                        color="light"
                        fontWeight="medium"
                      >
                        Email
                      </VuiTypography>
                    </VuiBox>

                    <GradientBorder
                      minWidth="100%"
                      padding="1px"
                      borderRadius={borders.borderRadius.lg}
                      backgroundImage={radialGradient(
                        palette.gradients.borderLight.main,
                        palette.gradients.borderLight.state,
                        palette.gradients.borderLight.angle
                      )}
                    >
                      <VuiInput
                        value={values.email}
                        name="email"
                        onChange={handleChange}
                        disabled={isLoading}
                        onBlur={handleBlur}
                        type="email"
                        sx={({ typography: { size } }) => ({
                          fontSize: size.sm,

                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(255, 255, 255, 0.2)",

                          "& input": {
                            color: "#fff !important",
                            WebkitTextFillColor: "#fff !important",
                          },
                        })}
                      />
                    </GradientBorder>

                    {touched.email && errors.email && (
                      <VuiTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.email}
                      </VuiTypography>
                    )}
                  </VuiBox>

                  {/* ================= PASSWORD FIELD (LOGIN MODE ONLY) ================= */}
                  {mode === "login" && (
                    <>
                      <VuiBox mb={2}>
                        <VuiBox mb={1} ml={0.5}>
                          <VuiTypography
                            component="label"
                            variant="button"
                            color="light"
                            fontWeight="medium"
                          >
                            Password
                          </VuiTypography>
                        </VuiBox>
                        <GradientBorder
                          minWidth="100%"
                          borderRadius={borders.borderRadius.lg}
                          padding="1px"
                          backgroundImage={radialGradient(
                            palette.gradients.borderLight.main,
                            palette.gradients.borderLight.state,
                            palette.gradients.borderLight.angle
                          )}
                        >
                          <VuiInput
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            disabled={loginLoading}
                            onBlur={handleBlur}
                            sx={({ typography: { size } }) => ({
                              fontSize: size.sm,
                              background:
                                "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))",
                              backdropFilter: "blur(20px)",
                              WebkitBackdropFilter: "blur(20px)",
                              border: "1px solid rgba(255, 255, 255, 0.2)",
                              boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.25)",
                              "& input": {
                                color: "#fff !important",
                                WebkitTextFillColor: "#fff !important",
                              },
                            })}
                          />
                        </GradientBorder>
                        {touched.password && errors.password && (
                          <VuiTypography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, ml: 0.5, fontSize: "11px" }}
                          >
                            {errors.password}
                          </VuiTypography>
                        )}
                      </VuiBox>

                      {/* Show Password + Forgot */}
                      <VuiBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1}
                      >
                        <VuiBox display="flex" alignItems="center">
                          <Checkbox
                            checked={showPassword}
                            onChange={(e) => setShowPassword(e.target.checked)}
                            size="small"
                            sx={{
                              padding: "2px !important",
                              "&.Mui-checked": { color: "#fff !important" },
                            }}
                          />
                          <VuiTypography
                            variant="button"
                            color="light"
                            sx={{ fontSize: "12px", opacity: 0.85 }}
                          >
                            Show password
                          </VuiTypography>
                        </VuiBox>

                        <VuiTypography
                          variant="caption"
                          color="light"
                          sx={{
                            fontSize: "12px",
                            opacity: 0.7,
                            cursor: "pointer",
                            "&:hover": { opacity: 1, textDecoration: "underline" },
                          }}
                          onClick={() => handleModeChange("forgot")}
                        >
                          Forgot password?
                        </VuiTypography>
                      </VuiBox>
                    </>
                  )}

                  {/* ================= ACTION BUTTON ================= */}
                  <VuiBox mt={4}>
                    <VuiButton
                      fullWidth
                      type="submit"
                      variant="outlined"
                      color="light"
                      disabled={isLoading}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {isLoading && (
                        <CircularProgress
                          size={16}
                          thickness={5}
                          sx={{ color: light.main, mr: 0.5 }}
                        />
                      )}

                      {buttonText}
                    </VuiButton>
                  </VuiBox>

                  {/* ================= BACK TO LOGIN ================= */}
                  {mode === "forgot" && (
                    <VuiBox mt={2} textAlign="center">
                      <VuiTypography
                        variant="caption"
                        color="light"
                        sx={{
                          fontSize: "12px",
                          opacity: 0.7,
                          cursor: "pointer",
                          "&:hover": { opacity: 1, textDecoration: "underline" },
                        }}
                        onClick={() => handleModeChange("login")}
                      >
                        Back to Sign In
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              );
            }}
          </Formik>
        </Grid>
      </Grid>
    </CoverLayout>
  );
}

export default SignIn;
